import * as CryptoJS from 'crypto-js';
import { getEnv } from '../../config/env';
import { apiFetch } from '../../utils/apiClient';
import { UrlBuilder } from './url-builder';
import logger from '../../utils/logger';
import {
    clearAllTokens,
    getCodeVerifier,
    setCodeVerifier,
    setTokens,
} from './tokenStore';

const Config = {
    ClientId: getEnv('VITE_CLIENT_ID') || '',
    ProviderBaseUrl: getEnv('VITE_AUTH_AUTHORIZATION_ENDPOINT'),
    ApplicationUrl: getEnv('VITE_APPLICATION_URL'),
    Scope: getEnv('VITE_OAUTH_SCOPE') || 'openid',
};

const QueryParams = {
    Scope: 'scope',
    ResponseType: 'response_type',
    ClientId: 'client_id',
    CodeChallenge: 'code_challenge',
    CodeChallengeMethod: 'code_challenge_method',
    CodeVerifier: 'code_verifier',
    Nonce: 'nonce',
    RedirectURI: 'redirect_uri',
    Code: 'code',
    PostLogoutRedirectURI: 'post_logout_redirect_uri',
    State: 'state',
    Logout: 'logout',
    S256: 'S256',
    External: 'external',
};

const AUTH_PROVIDER_PATHS = {
    AUTHORIZATION: '/as/authorization.oauth2',
    LOGOUT: '/idp/startSLO.ping',
};

const BACKEND_AUTH = {
    TOKENS: '/api/v1/auth/tokens',
    REFRESH: '/api/v1/auth/tokens/refresh',
};

const RANDOM_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const generateRandomString = (length) => {
    let random = '';
    const randomValues = Array.from(
        window.crypto.getRandomValues(new Uint8Array(length))
    );

    randomValues.forEach((value) => {
        random += RANDOM_CHARSET[value % RANDOM_CHARSET.length];
    });

    return random;
};

const generateCodeVerifier = () => generateRandomString(128);

const toBase64Url = (value) =>
    value
        .toString(CryptoJS.enc.Base64)
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_');

const generateCodeChallenge = (codeVerifier) =>
    toBase64Url(CryptoJS.SHA256(codeVerifier));

const trimTrailingSlashes = (value = '') => {
    let end = value.length;

    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }

    return value.slice(0, end);
};

const getRedirectUri = () => `${Config.ApplicationUrl}/`;

const getPostLogoutRedirectUri = () => `${Config.ApplicationUrl}/login`;

const getAuthorizationUrl = () =>
    `${trimTrailingSlashes(Config.ProviderBaseUrl)}${AUTH_PROVIDER_PATHS.AUTHORIZATION}`;

const getLogoutUrl = () =>
    `${trimTrailingSlashes(Config.ProviderBaseUrl)}${AUTH_PROVIDER_PATHS.LOGOUT}`;

const validateAuthConfig = () => {
    if (!Config.ProviderBaseUrl) {
        throw new Error(
            'Missing auth provider base URL. Set VITE_AUTH_AUTHORIZATION_ENDPOINT.'
        );
    }

    if (!Config.ApplicationUrl) {
        throw new Error('Missing VITE_APPLICATION_URL.');
    }

    if (!Config.ClientId) {
        throw new Error('Missing VITE_CLIENT_ID.');
    }
};

export const authProvider = {
    async getSignInRedirectUrl() {
        validateAuthConfig();

        const codeVerifier = generateCodeVerifier();
        const codeChallenge = generateCodeChallenge(codeVerifier);
        const nonce = generateRandomString(16);

        setCodeVerifier(codeVerifier);

        // Nonce is sent to the auth provider but not stored because it is not validated later.
        const redirectUrl = new UrlBuilder(getAuthorizationUrl())
            .withParam(QueryParams.Scope, Config.Scope)
            .withParam(QueryParams.ResponseType, QueryParams.Code)
            .withParam(QueryParams.ClientId, Config.ClientId)
            .withParam(QueryParams.CodeChallenge, codeChallenge)
            .withParam(QueryParams.CodeChallengeMethod, QueryParams.S256)
            .withParam(QueryParams.Nonce, nonce)
            .withParam(QueryParams.RedirectURI, getRedirectUri())
            .withParam(QueryParams.External, 'false')
            .build();

        return { redirectUrl };
    },

    async exchangeAuthorizationCode({ code }) {
        const codeVerifier = getCodeVerifier();

        const body = new URLSearchParams();
        body.set(QueryParams.Code, code);

        if (codeVerifier) {
            body.set(QueryParams.CodeVerifier, codeVerifier);
        }

        body.set(QueryParams.RedirectURI, getRedirectUri());

        const response = await apiFetch(BACKEND_AUTH.TOKENS, {
            method: 'POST',
            contentType: 'application/x-www-form-urlencoded',
            body: body.toString(),
            credentials: 'include',
            retry: true,
        });

        setTokens();

        return response;
    },

    async refreshSession() {
        return apiFetch(BACKEND_AUTH.REFRESH, {
            method: 'POST',
            credentials: 'include',
            retry: true,
        });
    },

    async logout() {
        validateAuthConfig();

        try {
            await apiFetch(BACKEND_AUTH.TOKENS, {
                method: 'DELETE',
                credentials: 'include',
                retry: false,
            });
        } catch (error) {
            logger.error('Error revoking auth session:', error);
        }

        clearAllTokens();

        const redirectUrl = new UrlBuilder(getLogoutUrl())
            .withParam(QueryParams.PostLogoutRedirectURI, getPostLogoutRedirectUri())
            .withParam(QueryParams.State, QueryParams.Logout)
            .build();

        return { redirectUrl };
    },
};